import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * LaunchGate — pre-launch visibility overlay.
 *
 * Wraps the whole app. On mount, it asks the backend whether the gate is
 * currently active (controlled by the `LAUNCH_GATE_PASSWORD` env var on the
 * backend). If active and the visitor has not yet unlocked, a full-screen
 * password prompt is rendered. On a successful unlock, a flag is stored in
 * localStorage so the user is not re-prompted on subsequent visits.
 *
 * To DISABLE the gate on launch day: set `LAUNCH_GATE_PASSWORD=` (empty) in
 * /app/backend/.env and `sudo supervisorctl restart backend`. No frontend
 * rebuild required.
 *
 * Security note: this is a visibility / "coming soon" gate, not real auth.
 * Real protection (member portal, paid downloads, admin) still flows through
 * JWT + tier checks elsewhere in the app. The password is validated
 * server-side and is never shipped in the JS bundle.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNLOCK_STORAGE_KEY = 'dhr-launch-unlocked';

export const LaunchGate = ({ children }) => {
  // 'checking' until /status responds; 'open' = no gate / unlocked;
  // 'locked' = gate active, password required.
  const [state, setState] = useState('checking');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Fast path: already unlocked this browser.
      if (typeof window !== 'undefined' && window.localStorage?.getItem(UNLOCK_STORAGE_KEY) === '1') {
        if (!cancelled) setState('open');
        return;
      }
      try {
        const { data } = await axios.get(`${API}/launch-gate/status`);
        if (cancelled) return;
        setState(data?.enabled ? 'locked' : 'open');
      } catch (e) {
        // If the status endpoint fails, fail open (show the site) — we never
        // want a backend hiccup to blank the entire production site.
        if (!cancelled) setState('open');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/launch-gate/unlock`, { password });
      try { window.localStorage.setItem(UNLOCK_STORAGE_KEY, '1'); } catch (_) { /* private mode */ }
      setState('open');
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setError('Incorrect password.');
      else if (status === 404) {
        // Gate was disabled between the /status check and now — just open.
        setState('open');
      } else setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'checking') {
    // Render nothing during the very brief status probe so we don't flash the
    // site contents and then yank them away.
    return null;
  }
  if (state === 'open') return children;

  // state === 'locked'
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4"
      style={{ backgroundImage: 'linear-gradient(135deg, #1A56E8 0%, #0E2A6E 100%)' }}
      data-testid="launch-gate"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 sm:p-10">
        <div className="text-center mb-6">
          <div className="inline-block text-2xl font-bold mb-2" style={{ color: '#1A56E8' }}>
            DriveHR
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Launching Soon
          </h1>
          <p className="text-gray-600 text-sm">
            Our site is being finalized. Enter the access password to preview.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="launch-gate-password" className="sr-only">Password</label>
            <input
              id="launch-gate-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Access password"
              className="w-full h-11 px-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              data-testid="launch-gate-input"
            />
          </div>
          {error && (
            <div className="text-sm text-red-600" data-testid="launch-gate-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting || !password}
            className="w-full h-11 rounded-lg font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#1A56E8' }}
            data-testid="launch-gate-submit"
          >
            {submitting ? 'Verifying...' : 'Enter Site'}
          </button>
        </form>
        <p className="text-xs text-gray-400 text-center mt-6">
          Authorized preview only. Please do not share this URL.
        </p>
      </div>
    </div>
  );
};
